/**
 * Canonical org-chart canvas node-type membership.
 *
 * One source of truth for "is this node part of the org structure?" and for
 * the variant groups inside it. Before this existed the same lists were
 * hand-maintained in seven places (layout, edge anchoring, search, selection,
 * assistant, panel routing, canvas plugin), and every new type missed at
 * least one — `org_open_seat` shipped rendering correctly but parked at the
 * canvas origin because the layout filter never learned it.
 *
 * Ontology alignment (docs/architecture/universal-ontology.md): these are
 * CANVAS PRESENTATION types, not ontology entities. Several canvas types
 * project one logical entity — six person card variants are all the ontology
 * `Actor(person)`; `org_position` and `org_open_seat` are both the ontology
 * `Position/Seat` (an open seat is a Position with no active `occupies`, a
 * state, not a new entity). Record `nodeType` values and `orgEntityKind`
 * mapping stay in ontology terms and must never learn presentation-only
 * types like `org_open_seat`.
 */

/** Card variants of one logical Person (ontology: Actor(person)). */
export const ORG_PERSON_NODE_TYPES: ReadonlySet<string> = new Set([
  'org_person',
  'org_person_photo',
  'org_person_photo_left',
  'org_person_photo_right',
  'org_person_photo_inner_left',
  'org_person_text',
]);

/**
 * Card variants of one logical Position (ontology: Position/Seat).
 * `org_open_seat` is the compact vacant-seat card projected onto live people
 * charts — locked projection only, never authored, never a record nodeType.
 */
export const ORG_POSITION_NODE_TYPES: ReadonlySet<string> = new Set([
  'org_position',
  'org_open_seat',
]);

/** Everything that participates in the org structure on a canvas. */
export const ORG_NODE_TYPES: ReadonlySet<string> = new Set([
  ...ORG_PERSON_NODE_TYPES,
  ...ORG_POSITION_NODE_TYPES,
  'org_department',
]);

export const isOrgPersonNodeType = (type: string | undefined | null): boolean =>
  type != null && ORG_PERSON_NODE_TYPES.has(type);

export const isOrgPositionNodeType = (type: string | undefined | null): boolean =>
  type != null && ORG_POSITION_NODE_TYPES.has(type);

export const isOrgNodeType = (type: string | undefined | null): boolean =>
  type != null && ORG_NODE_TYPES.has(type);

/** A person card projected from folder truth (locked to the projection). */
export const isProjectedOrgPersonCard = (node: {
  type: string;
  locked?: boolean;
  lockOwner?: string;
}): boolean => node.locked === true && node.lockOwner === 'projection' && isOrgPersonNodeType(node.type);

/**
 * True when the scene is a people chart projected from folder truth. There a
 * new Position is a vacancy, so it presents as an open seat.
 */
export const isProjectedPeopleChartScene = (
  nodes: ReadonlyArray<{ type: string; locked?: boolean; lockOwner?: string }>,
): boolean => nodes.some(isProjectedOrgPersonCard);

/**
 * True when any org card is projected from folder truth. Edits there commit
 * to the organization model, which has no standalone department record.
 */
export const isProjectedOrgChartScene = (
  nodes: ReadonlyArray<{ type: string; locked?: boolean; lockOwner?: string }>,
): boolean => nodes.some((node) => node.locked === true && node.lockOwner === 'projection' && isOrgNodeType(node.type));
